import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, authHeaders, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

export default function ScheduleTab() {
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/schedule`)
      .then((res) => setEntries(res.data))
      .catch(() => toast.error("Could not load schedule"));
  }, []);

  const update = (index, key, value) =>
    setEntries((list) => list.map((e) => (e.day_index === index ? { ...e, [key]: value } : e)));

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/schedule`, { entries }, authHeaders());
      setEntries(res.data);
      toast.success("Schedule updated - live on the site now");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-schedule-tab">
      <p className="text-sm text-zinc-400 mb-6">
        Update where the cart will be each day. The map uses the "Map location" field - a street address or landmark works best.
      </p>

      <div className="space-y-4">
        {entries.map((entry) => (
          <div
            key={entry.day_index}
            data-testid={`schedule-edit-row-${entry.day_index}`}
            className="border border-white/10 bg-disco-surface/50 p-5 grid gap-4 sm:grid-cols-[120px_1fr_1fr] items-start"
          >
            <div>
              <p className="font-display uppercase tracking-wider text-xl text-[#F8F8F8]">{entry.day}</p>
              <label className="mt-2 flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                <Checkbox
                  data-testid={`schedule-closed-${entry.day_index}`}
                  checked={entry.closed}
                  onCheckedChange={(v) => update(entry.day_index, "closed", Boolean(v))}
                  className="border-white/20 data-[state=checked]:bg-disco-lavender data-[state=checked]:border-disco-lavender"
                />
                Closed / private
              </label>
            </div>
            <div className="space-y-3">
              <Input
                data-testid={`schedule-location-${entry.day_index}`}
                value={entry.location}
                onChange={(e) => update(entry.day_index, "location", e.target.value)}
                placeholder="Location name (e.g. Neon Alley - 5th & Main)"
                className="bg-white/5 border-white/10 text-zinc-100 text-sm"
              />
              <Input
                data-testid={`schedule-hours-${entry.day_index}`}
                value={entry.hours}
                onChange={(e) => update(entry.day_index, "hours", e.target.value)}
                placeholder="Hours (e.g. 5pm - 11pm)"
                className="bg-white/5 border-white/10 text-zinc-100 text-sm"
              />
            </div>
            <div className="space-y-3">
              <Input
                data-testid={`schedule-map-${entry.day_index}`}
                value={entry.map_query}
                onChange={(e) => update(entry.day_index, "map_query", e.target.value)}
                placeholder="Map location (address or landmark)"
                className="bg-white/5 border-white/10 text-zinc-100 text-sm"
              />
              <Input
                data-testid={`schedule-note-${entry.day_index}`}
                value={entry.note}
                onChange={(e) => update(entry.day_index, "note", e.target.value)}
                placeholder="Short note for customers (optional)"
                className="bg-white/5 border-white/10 text-zinc-100 text-sm"
              />
            </div>
          </div>
        ))}
      </div>

      <button
        data-testid="schedule-save-button"
        onClick={save}
        disabled={saving || entries.length === 0}
        className="mt-8 rounded-full bg-disco-yellow text-disco-void font-semibold px-8 py-3 text-sm transition-all duration-300 hover:shadow-[0_0_24px_rgba(226,255,0,0.4)] active:scale-95 disabled:opacity-50"
      >
        {saving ? "Saving..." : "Save Schedule"}
      </button>
    </div>
  );
}
